import {
  CheckCircle2, Loader2, Lock, ArrowRight, Upload as UploadIcon,
  ClipboardList, Sparkles, Eye, ShieldCheck,
} from "lucide-react";

/**
 * <ActionPlan/> — guided, ordered journey shown at the top of the client portal.
 *
 * Reads the `action_plan` block already embedded in the portal response. Each
 * step shows: number, title, description, status pill, "missing" subtext,
 * and an action button that scrolls to the target component below (where the
 * client actually completes the work — uploads, form, or document view).
 */
export default function ActionPlan({ plan, onAction }) {
  if (!plan || !plan.steps || plan.steps.length === 0) return null;

  const clientSteps = plan.steps.filter((s) => s.action_kind !== "milestone");
  const firmSteps   = plan.steps.filter((s) => s.action_kind === "milestone");

  // Progress shown in the header focuses on the client journey (what THEY
  // can act on). Firm milestones are summarised separately in their group.
  const clientTotal = clientSteps.length;
  const clientDone  = clientSteps.filter((s) => s.status === "complete").length;
  const pct = clientTotal ? Math.round((clientDone / clientTotal) * 100) : 0;
  const allClientDone = clientTotal > 0 && clientDone === clientTotal;

  const scrollTo = (anchor) => {
    if (!anchor) return;
    const el = document.querySelector(`[data-testid="${anchor}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      onAction?.();
    }
  };

  return (
    <div className="bg-white border border-zinc-200" data-testid="action-plan">
      <div className="px-6 py-5 border-b border-zinc-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 bg-[#0055FF] text-white grid place-items-center mt-0.5">
              <ClipboardList className="h-4 w-4" />
            </div>
            <div>
              <div className="text-overline text-zinc-500 flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> Your action plan
              </div>
              <h2 className="font-serif text-3xl tracking-tight mt-1">
                {allClientDone ? "All your steps are done" : "Here's what to do next"}
              </h2>
              <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
                {allClientDone
                  ? "Nice work — your part is complete. The firm milestones below show what we're handling next."
                  : "Follow the steps below in order. Each step is linked to your legal requirements — completing one updates the other automatically."}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span
              className={`text-[10px] tracking-widest uppercase px-2 py-1 ${
                allClientDone
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-zinc-900 text-white"
              }`}
              data-testid="action-plan-pill"
            >
              {allClientDone
                ? "Your part is done"
                : `Step ${clientStepNumber(plan, clientSteps)} of ${clientTotal}`}
            </span>
            <div className="text-xs text-zinc-500 mt-2" data-testid="action-plan-progress-text">
              {clientDone} of {clientTotal} complete · {pct}%
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className="h-2 bg-zinc-100 rounded-full overflow-hidden" data-testid="action-plan-progress-bar">
            <div
              className={`h-full transition-all duration-500 ${allClientDone ? "bg-emerald-500" : "bg-[#0055FF]"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      {/* ===== Your steps (client) ===== */}
      {clientSteps.length > 0 && (
        <section data-testid="client-steps-section">
          <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-100 text-overline text-zinc-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ClipboardList className="h-3.5 w-3.5" /> Your steps
            </span>
            <span className="text-[10px] tracking-widest uppercase text-zinc-500">
              {clientDone}/{clientTotal}
            </span>
          </div>
          <ol className="divide-y divide-zinc-100">
            {clientSteps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
                locked={step.action_kind === "review" && hasEarlierIncomplete(clientSteps, step)}
                isNext={plan.next_step_id === step.id}
                onAction={() => scrollTo(step.anchor)}
              />
            ))}
          </ol>
        </section>
      )}

      {/* ===== Handled by EH Inc (firm milestones) ===== */}
      {firmSteps.length > 0 && (
        <section data-testid="firm-milestones-section" className="bg-zinc-50/60 border-t-2 border-zinc-900">
          <div className="px-6 py-3 bg-zinc-900 text-white text-overline flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Handled by EH Inc
            </span>
            <span className="text-[10px] tracking-widest uppercase opacity-70">
              {firmSteps.filter((s) => s.status === "complete").length}/{firmSteps.length}
            </span>
          </div>
          <ol className="divide-y divide-zinc-200">
            {firmSteps.map((step) => (
              <MilestoneRow key={step.id} step={step} />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

function clientStepNumber(plan, clientSteps) {
  // Highlight the first non-complete client step.
  const target = clientSteps.find((s) => s.id === plan.next_step_id)
              || clientSteps.find((s) => s.status !== "complete");
  if (!target) return clientSteps.length;
  // Re-number from the client-only list (1..N) so the header reads cleanly.
  const idx = clientSteps.findIndex((s) => s.id === target.id);
  return idx + 1;
}

function hasEarlierIncomplete(steps, step) {
  const idx = steps.findIndex((s) => s.id === step.id);
  return steps.slice(0, idx).some((s) => s.status !== "complete");
}

// ---------------------------------------------------------------------------

const STATUS_META = {
  complete:    { pill: "bg-emerald-100 text-emerald-800", label: "Complete",      Icon: CheckCircle2, ring: "bg-emerald-600 text-white" },
  in_progress: { pill: "bg-amber-100  text-amber-900",   label: "In progress",   Icon: Loader2,      ring: "bg-amber-500 text-white" },
  required:    { pill: "bg-zinc-100   text-zinc-800",    label: "Required",      Icon: ClipboardList, ring: "bg-zinc-900 text-white" },
};

const ACTION_ICON = {
  upload:    UploadIcon,
  form:      ClipboardList,
  review:    Eye,
  milestone: ShieldCheck,
};

function StepRow({ step, locked, isNext, onAction }) {
  const meta = STATUS_META[step.status] || STATUS_META.required;
  const ActionIcon = ACTION_ICON[step.action_kind] || ArrowRight;

  const isComplete = step.status === "complete";
  const rowBg =
    isComplete ? "bg-emerald-50/30" :
    isNext     ? "bg-[#0055FF]/5" :
                 "bg-white";

  return (
    <li
      className={`px-6 py-5 flex items-start gap-4 flex-wrap ${rowBg}`}
      data-testid={`action-step-${step.id}`}
    >
      {/* Step circle */}
      <div className={`shrink-0 h-10 w-10 rounded-full grid place-items-center font-semibold ${meta.ring}`}>
        {isComplete ? (
          <CheckCircle2 className="h-5 w-5" />
        ) : (
          <span className="text-sm">{step.step_number}</span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[10px] tracking-widest uppercase text-zinc-500 font-medium">
            Step {step.step_number}
          </div>
          <span
            className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 ${meta.pill}`}
            data-testid={`action-step-status-${step.id}`}
          >
            {meta.label}
          </span>
          {isNext && !isComplete && (
            <span className="text-[10px] tracking-widest uppercase bg-[#0055FF] text-white px-1.5 py-0.5">
              Do this next
            </span>
          )}
          {step.progress?.total > 0 && (
            <span className="text-[10px] text-zinc-500 font-mono">
              {step.progress.complete}/{step.progress.total}
            </span>
          )}
        </div>
        <div className="font-medium text-zinc-900 mt-1">{step.title}</div>
        <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{step.description}</p>
        {step.missing && step.missing.length > 0 && !isComplete && (
          <ul className="mt-2 text-xs text-amber-800 list-disc pl-5 space-y-0.5">
            {step.missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        )}
      </div>

      {/* Action button */}
      <div className="shrink-0">
        {locked ? (
          <span className="inline-flex items-center gap-1 text-xs text-zinc-400 italic">
            <Lock className="h-3 w-3" /> Locked
          </span>
        ) : isComplete ? (
          <button
            onClick={onAction}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 underline"
            data-testid={`action-step-btn-${step.id}`}
          >
            <Eye className="h-3 w-3" /> View
          </button>
        ) : (
          <button
            onClick={onAction}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 ${
              isNext
                ? "bg-[#0055FF] hover:bg-[#003ECC] text-white"
                : "bg-zinc-900 hover:bg-zinc-800 text-white"
            }`}
            data-testid={`action-step-btn-${step.id}`}
          >
            <ActionIcon className="h-3.5 w-3.5" />
            {step.action_label}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Firm-handled milestone row — visually distinct from client steps.
// Compact, monochrome, no clickable action; emphasises status, not user task.
// ---------------------------------------------------------------------------

function MilestoneRow({ step }) {
  const isComplete = step.status === "complete";
  const isInProgress = step.status === "in_progress";

  const dot = isComplete
    ? "bg-emerald-600 text-white"
    : isInProgress
      ? "bg-amber-500 text-white"
      : "bg-zinc-300 text-zinc-700";

  const statusText = isComplete
    ? "Completed by EH Inc"
    : isInProgress
      ? "In progress with EH Inc"
      : "Awaiting your earlier steps";

  return (
    <li
      className="px-6 py-4 flex items-start gap-4 flex-wrap bg-white"
      data-testid={`action-step-${step.id}`}
    >
      <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center ${dot}`}>
        {isComplete
          ? <CheckCircle2 className="h-4 w-4" />
          : <ShieldCheck className="h-4 w-4" />}
      </div>

      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] tracking-widest uppercase bg-zinc-900 text-white px-1.5 py-0.5">
            Handled by EH Inc
          </span>
          {isInProgress && (
            <span className="text-[10px] tracking-widest uppercase bg-amber-100 text-amber-900 px-1.5 py-0.5 inline-flex items-center gap-1">
              <Loader2 className="h-2.5 w-2.5 animate-spin" /> In progress
            </span>
          )}
          {isComplete && (
            <span className="text-[10px] tracking-widest uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
              Complete
            </span>
          )}
        </div>
        <div className="font-medium text-zinc-900 mt-1">{step.title}</div>
        <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{step.description}</p>
        {step.completed_at && (
          <p className="text-xs text-emerald-700 mt-1.5">
            ✓ Completed {new Date(step.completed_at).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
            {step.completed_by ? ` by ${step.completed_by}` : ""}
            {step.note ? ` — ${step.note}` : ""}
          </p>
        )}
      </div>

      <span className={`shrink-0 inline-flex items-center gap-1 text-xs italic ${
        isComplete ? "text-emerald-700" : isInProgress ? "text-amber-700" : "text-zinc-400"
      }`}>
        {statusText}
      </span>
    </li>
  );
}
